import DOMPurify from "dompurify";
import {
  EMPTY_STRING,
  MIN_ORG_NAME_LENGTH,
  SUSPICIOUS_NAME_TOKENS
} from "src/components/constant/constant";

const RICH_TEXT_ALLOWED_TAGS = [
  "b",
  "i",
  "em",
  "strong",
  "u",
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "a",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "span"
];

const RICH_TEXT_ALLOWED_ATTR = ["href", "target", "rel"];

export const sanitizeRichHtml = (html?: string | null): string => {
  if (!html) return EMPTY_STRING;

  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR
  });
};

export const userNameValidation = (input: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!input) {
    return "Username is required";
  }

  if (emailRegex.test(input)) {
    return "The username cannot be an email address.";
  } else {
    return EMPTY_STRING;
  }
};

export const emailValidation = (input: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  return emailRegex.test(input);
};

export const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  return uuidRegex.test(str);
};

export const hasHTML = (str: string) => {
  const htmlRegex = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>|<([A-Za-z][A-Za-z0-9]*)\b[^>]*\/>/;

  return htmlRegex.test(str);
};

const suspiciousTokenRegex = new RegExp(`\\b(?:${SUSPICIOUS_NAME_TOKENS.join("|")})\\b`, "gi");

export const sanitizeName = (name?: string | null): string => {
  if (!name) return EMPTY_STRING;

  return name.replace(suspiciousTokenRegex, EMPTY_STRING).replace(/\s+/g, " ").trim();
};

export const isValidOrgName = (name?: string | null, minLength: number = MIN_ORG_NAME_LENGTH): boolean => {
  const sanitized = sanitizeName(name);

  if (!sanitized || sanitized.length < minLength) return false;

  const lower = sanitized.toLowerCase();

  if (SUSPICIOUS_NAME_TOKENS.some((token) => token === lower)) return false;
  if (suspiciousTokenRegex.test(sanitized)) {
    suspiciousTokenRegex.lastIndex = 0;
    return false;
  }
  suspiciousTokenRegex.lastIndex = 0;
  return true;
};

// Same rule as the backend's isWholeCents: at most 2 decimal places, with a float tolerance.
export const isWholeCents = (value: number): boolean => {
  if (!Number.isFinite(value)) return false;
  const cents = value * 100;

  return Math.abs(cents - Math.round(cents)) < 1e-6;
};
